import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Shield, Inbox, CheckCircle, Settings, LogOut, Users,
  FileText, BarChart3, Tag, Mail, Camera, Globe, Database,
  ArrowLeft, Rocket, Grid3X3, Lock, ShieldOff, Activity, FileCheck, Timer, Download,
} from 'lucide-react';

const TEAM_NAV = [
  { section: 'Review', items: [
    { label: 'Queue', path: '/team', icon: Inbox },
    { label: 'Completed', path: '/team/completed', icon: CheckCircle },
    { label: 'Pending Orders', path: '/team/orders/pending', icon: Timer, requiresFullAdmin: true },
  ]},
  { section: 'Catalog', items: [
    { label: 'Brands', path: '/team/brands', icon: Tag, requiresFullAdmin: true },
    { label: 'Categories', path: '/team/categories', icon: Grid3X3, requiresFullAdmin: true },
    { label: 'Image Rules', path: '/team/image-rules', icon: Camera, requiresFullAdmin: true },
    { label: 'Brand Pages', path: '/team/brand-pages', icon: Globe, requiresFullAdmin: true },
    { label: 'Offline Certs', path: '/team/offline-certs', icon: FileCheck, requiresFullAdmin: true },
  ]},
  { section: 'Admin', items: [
    { label: 'Reports', path: '/team/reports', icon: BarChart3, requiresFullAdmin: true },
    { label: 'CMS Pages', path: '/team/cms', icon: FileText, requiresFullAdmin: true },
    { label: 'Emails', path: '/team/emails', icon: Mail, requiresFullAdmin: true },
    { label: 'Documents', path: '/team/documents', icon: FileCheck, requiresFullAdmin: true },
    { label: 'Email Diagnostics', path: '/team/email-diagnostics', icon: Activity, requiresFullAdmin: true },
    { label: 'Coupons', path: '/team/coupons', icon: Tag, requiresFullAdmin: true },
    { label: 'Users', path: '/team/users', icon: Users, requiresFullAdmin: true },
    { label: 'Privacy', path: '/team/privacy', icon: ShieldOff, requiresFullAdmin: true },
    { label: 'Settings', path: '/team/settings', icon: Settings, requiresFullAdmin: true },
    { label: 'Imports', path: '/team/imports', icon: Database, requiresFullAdmin: true },
    { label: 'Launch', path: '/team/launch', icon: Rocket, requiresFullAdmin: true },
    { label: 'Exports', path: '/team/exports', icon: Download, requiresFullAdmin: true },
    { label: 'Perf', path: '/team/debug/perf', icon: Activity, requiresFullAdmin: true },
  ]},
];

const FULL_ADMIN_PATHS = TEAM_NAV.flatMap((group) => group.items)
  .filter((item) => item.requiresFullAdmin)
  .map((item) => item.path);

export default function TeamLayout() {
  const { isAuthenticated, canAccessTeam, isFullAdmin, loading, user, logout } = useAuth();
  const location = useLocation();
  const blockedFullAdminArea = !isFullAdmin && FULL_ADMIN_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const visibleNav = TEAM_NAV
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.requiresFullAdmin || isFullAdmin),
    }))
    .filter((group) => group.items.length > 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated || !canAccessTeam) {
    return <Navigate to="/account/sign-in" replace />;
  }

  if (blockedFullAdminArea) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'hsl(var(--bg-paper))' }} data-testid="team-access-blocked-state">
        <div className="max-w-md w-full bg-white border border-stone/50 rounded-2xl p-6 text-center space-y-4">
          <div className="rounded-full h-12 w-12 mx-auto flex items-center justify-center" style={{ background: 'rgba(163,106,58,0.12)' }}>
            <Lock className="h-5 w-5" style={{ color: '#A36A3A' }} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-medium text-ink">Full admin access required</h1>
            <p className="text-sm text-muted-foreground mt-2">Your staff account can use review tools, but this section is reserved for full admins.</p>
          </div>
          <Link to="/team" className="inline-flex">
            <Button className="gap-2 rounded-[12px]" data-testid="team-access-blocked-back-button">
              <ArrowLeft className="h-4 w-4" /> Back to Queue
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'hsl(var(--bg-paper))' }}>
      {/* Sidebar */}
      <aside className="w-52 flex-shrink-0 border-r border-stone bg-white/80 hidden lg:flex flex-col">
        <div className="px-3 py-3 flex items-center gap-2 border-b border-stone/50">
          <Link to="/" className="flex items-center gap-2 no-underline">
            <Shield className="h-4 w-4 text-ink" strokeWidth={1.5} />
            <span className="font-display text-sm font-medium text-ink">{isFullAdmin ? 'TFD Admin' : 'TFD Team'}</span>
          </Link>
        </div>
        <ScrollArea className="flex-1 py-2">
          {visibleNav.map((group) => (
            <div key={group.section} className="px-2 mb-3">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium px-2 mb-1">{group.section}</p>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path || (item.path !== '/team' && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs no-underline transition-colors duration-150 ${
                      active ? 'bg-secondary text-ink font-medium' : 'text-muted-foreground hover:text-ink hover:bg-secondary/60'
                    }`}
                    data-testid={`team-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </ScrollArea>
        <div className="px-3 py-2 border-t border-stone/50">
          <p className="text-[10px] text-muted-foreground truncate mb-1">{user?.email}</p>
          <Button variant="ghost" size="sm" onClick={logout} className="w-full justify-start gap-1.5 text-xs h-7 text-muted-foreground">
            <LogOut className="h-3 w-3" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone lg:hidden">
          <div className="px-3 h-11 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-ink" strokeWidth={1.5} />
              <span className="font-display text-sm font-medium text-ink">TFD</span>
              <Separator orientation="vertical" className="h-4" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{isFullAdmin ? 'Full Admin' : 'Team'}</span>
            </div>
            <div className="flex items-center gap-1 overflow-x-auto">
              {visibleNav.flatMap(g => g.items).slice(0, 5).map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} className="no-underline">
                    <Button variant={active ? 'secondary' : 'ghost'} size="sm" className="gap-1 text-[10px] h-7 px-2">
                      <Icon className="h-3 w-3" />
                      <span className="hidden sm:inline">{item.label}</span>
                    </Button>
                  </Link>
                );
              })}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-3 sm:p-4 max-w-[1200px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
